import React from 'react';
import { Download, FileArchive, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const DownloadPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0B1D2A] to-black">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D4AF37]/10 mb-6">
              <FileArchive className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 
              className="text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Charles Crawshaw Portfolio
            </h1>
            <p 
              className="text-xl text-white/70"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Production-Ready Export Package
            </p>
          </div>

          {/* Main Card */}
          <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-8 backdrop-blur-sm mb-8">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-8 pb-8 border-b border-[#D4AF37]/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#D4AF37] mb-2">804 KB</div>
                <div className="text-sm text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>File Size</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#D4AF37] mb-2">525</div>
                <div className="text-sm text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>Files</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#D4AF37] mb-2">100%</div>
                <div className="text-sm text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>Complete</div>
              </div>
            </div>

            {/* File Info */}
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center py-3 border-b border-[#D4AF37]/10">
                <span className="text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>Filename</span>
                <span className="text-[#D4AF37] font-semibold" style={{ fontFamily: 'Lato, sans-serif' }}>charles-portfolio-final.zip</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-[#D4AF37]/10">
                <span className="text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>Status</span>
                <span className="text-green-400 font-semibold flex items-center gap-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <CheckCircle className="w-4 h-4" />
                  Production Ready
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-white/60" style={{ fontFamily: 'Lato, sans-serif' }}>MD5 Checksum</span>
                <span className="text-white/80 font-mono text-sm">a70d37823...2e90bc3</span>
              </div>
            </div>

            {/* Download Buttons */}
            <div className="space-y-4">
              <a
                href="/charles-portfolio-final.zip"
                download
                className="block"
              >
                <Button
                  className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download ZIP File
                </Button>
              </a>

              <a
                href="/download.html"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  variant="outline"
                  className="w-full border-2 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 py-6 text-lg rounded-lg transition-all duration-300"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  View Download Page
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </Card>

          {/* What's Included */}
          <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-8 backdrop-blur-sm mb-8">
            <h2 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              📦 What's Included
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-[#D4AF37] font-semibold mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                  ✅ Complete Frontend
                </h3>
                <ul className="space-y-2 text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <li>• React 19 with all sections</li>
                  <li>• 40+ shadcn/ui components</li>
                  <li>• Mobile responsive design</li>
                  <li>• SEO optimized</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#D4AF37] font-semibold mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                  ✅ Python Backend
                </h3>
                <ul className="space-y-2 text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <li>• FastAPI with contact form</li>
                  <li>• Admin dashboard</li>
                  <li>• Email notifications</li>
                  <li>• Security features</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#D4AF37] font-semibold mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                  ✅ Documentation
                </h3>
                <ul className="space-y-2 text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <li>• Complete deployment guide</li>
                  <li>• API documentation</li>
                  <li>• Testing procedures</li>
                  <li>• Setup instructions</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#D4AF37] font-semibold mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                  ✅ Configuration
                </h3>
                <ul className="space-y-2 text-white/70 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <li>• package.json & requirements.txt</li>
                  <li>• Environment templates</li>
                  <li>• Tailwind config</li>
                  <li>• All setup files</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Next Steps */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
              After Download
            </h3>
            <ol className="space-y-2 text-white/80 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
              <li>1. Extract the ZIP file</li>
              <li>2. Read <code className="bg-black/30 px-2 py-1 rounded">app/PACKAGE_README.md</code> for quick start</li>
              <li>3. Follow <code className="bg-black/30 px-2 py-1 rounded">app/DEPLOYMENT_GUIDE.md</code> for deployment</li>
              <li>4. Deploy to Hostinger VPS and launch charlescrawshaw.com!</li>
            </ol>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-white/60 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
            <p>🌍 Creating a World That Works for Everyone</p>
            <p className="mt-2">Charles Crawshaw Global Movement Platform</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
